/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: debugMode.ts (Debug Mode Store)
 🗓️ Created: 2025-01-22
 🧩 Description:
    Global store for Debug Mode state management.
    Provides a centralized way to track and update debug mode
    without constantly querying the API.

 🔧 Notes:
    - Syncs with user profile debugModeActive state
    - Provides reactive updates across components
    - Avoids unnecessary API calls

 🔁 Last Updated: 2025-01-22 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DebugModeState {
  isActive: boolean;
  hasPermission: boolean;
  setDebugMode: (isActive: boolean, hasPermission: boolean) => void;
  toggleDebugMode: () => void;
  initializeFromUser: (user: { debugMode?: boolean; debugModeActive?: boolean } | null | undefined) => void;
  syncWithUser: (user: { debugMode?: boolean; debugModeActive?: boolean } | null | undefined) => void;
}

export const useDebugModeStore = create<DebugModeState>()(
  persist(
    (set, get) => ({
      isActive: false,
      hasPermission: false,

      setDebugMode: (isActive: boolean, hasPermission: boolean) => {
        set({ isActive, hasPermission });
      },

      toggleDebugMode: () => {
        const { isActive, hasPermission } = get();
        if (hasPermission) {
          set({ isActive: !isActive });
        }
      },

      initializeFromUser: (user: { debugMode?: boolean; debugModeActive?: boolean } | null | undefined) => {
        const debugMode = user?.debugMode || false;
        const debugModeActive = user?.debugModeActive || false;
        
        // Always update permission from user data
        // Set isActive based on user's debugModeActive state
        set({ 
          isActive: debugModeActive, 
          hasPermission: debugMode 
        });
      },

      syncWithUser: (user: { debugMode?: boolean; debugModeActive?: boolean } | null | undefined) => {
        const debugMode = user?.debugMode || false;
        const debugModeActive = user?.debugModeActive || false;
        
        // Update both permission and active state from user data
        // This ensures the store stays in sync with the backend
        set({ 
          isActive: debugModeActive, 
          hasPermission: debugMode 
        });
      }
    }),
    {
      name: 'debug-mode-storage',
      partialize: (state) => ({ 
        isActive: state.isActive, 
        hasPermission: state.hasPermission 
      }),
    }
  )
);
